import { FC, useState, MouseEvent, useMemo, useContext } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Box,
} from '@material-ui/core';
import AssistantIcon from '@material-ui/icons/Assistant';
import StarIcon from '@material-ui/icons/Star';
import { useChat } from '../../contexts';
import { FavoritesContext } from '../../contexts/FavoritesContext';
import { ConversationAssistant, Assistant } from '../../api/MgaApi';
import {
  LOCAL_ASSISTANT_INFOS,
  LOCAL_ASSISTANT_IDS,
  isLocalAssistantId,
} from '../../api/LocalAssistants';
import { featuredAssistantIds } from '../../constants';
import { useApi } from '@backstage/core-plugin-api';
import { mgaApiRef } from '../../api/MgaApi';
import { getAssistantLogoUrl } from '../../util';
import useSWR from 'swr';

const useStyles = makeStyles((theme: Theme) => ({
  selectorButton: {
    padding: theme.spacing(0.5),
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      '& $avatarWrapper': {
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
      },
    },
  },
  avatarWrapper: {
    borderRadius: '50%',
    transition: 'box-shadow 0.2s ease',
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: 'pointer',
  },
  menu: {
    marginTop: theme.spacing(1),
    '& .MuiPaper-root': {
      minWidth: 250,
      maxHeight: 400,
      overflowY: 'auto',
    },
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    marginRight: theme.spacing(1.5),
  },
  sectionHeader: {
    padding: theme.spacing(0.5, 2),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  assistantName: {
    fontWeight: 500,
  },
  starIcon: {
    fontSize: 16,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.warning.main,
  },
}));

interface AssistantSelectorProps {
  disabled?: boolean;
}

// Helper hook to safely use favorites
const useFavoritesOptional = (): ConversationAssistant[] => {
  const context = useContext(FavoritesContext);
  // Return empty array if context doesn't exist
  return context?.favorites || [];
};

export const AssistantSelector: FC<AssistantSelectorProps> = ({
  disabled = false,
}) => {
  const classes = useStyles();
  const mgaApi = useApi(mgaApiRef);
  const { pendingAssistant, prepareNewConversation, activeConversation } =
    useChat();
  const favorites = useFavoritesOptional();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // current assistant is either from active conversation or pending assistant
  const currentAssistant =
    activeConversation.data?.assistant || pendingAssistant;

  // fetch featured assistants
  const { data: featuredAssistants = [] } = useSWR<ConversationAssistant[]>(
    'assistant-selector-featured',
    async () => {
      const assistants: ConversationAssistant[] = [...LOCAL_ASSISTANT_INFOS];

      const assistantPromises = featuredAssistantIds.map(async id => {
        try {
          const assistant = await mgaApi.getAssistantById(id);
          const result: ConversationAssistant = {
            id: assistant.id,
            name: assistant.name,
            description: assistant.description,
            logoUrl: assistant.logo_url,
            model:
              typeof assistant.model === 'object'
                ? assistant.model.model
                : assistant.model,
            topic: assistant.topic,
            instructions: assistant.instructions,
          };
          return result;
        } catch {
          // Return null on fetch error - UI will filter out failed assistant requests
          return null;
        }
      });

      const mgaAssistants = await Promise.all(assistantPromises);
      return [
        ...assistants,
        ...mgaAssistants.filter(Boolean),
      ] as ConversationAssistant[];
    },
    { revalidateOnFocus: false },
  );

  // combine and deduplicate assistants
  const allAssistants = useMemo(() => {
    const assistantMap = new Map<string, ConversationAssistant>();

    // add featured assistants
    featuredAssistants.forEach(assistant => {
      assistantMap.set(assistant.id, assistant);
    });

    // add favorited assistants
    favorites.forEach(assistant => {
      if (!assistantMap.has(assistant.id)) {
        assistantMap.set(assistant.id, assistant);
      }
    });

    return {
      featured: featuredAssistants,
      favorited: favorites.filter(
        fav =>
          !featuredAssistantIds.includes(fav.id) &&
          !LOCAL_ASSISTANT_IDS.includes(fav.id),
      ),
    };
  }, [featuredAssistants, favorites]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAssistant = (assistant: ConversationAssistant) => {
    prepareNewConversation(assistant);
    handleClose();
  };

  const getAssistantAvatar = (
    assistant: ConversationAssistant | Assistant | null,
  ) => {
    if (!assistant) return null;

    if (isLocalAssistantId(assistant.id)) {
      const logoUrl = getAssistantLogoUrl(assistant);
      if (logoUrl) {
        return (
          <Avatar className={classes.avatar} src={logoUrl} alt={assistant.name} />
        );
      }
      return (
        <Avatar className={classes.avatar} style={{ backgroundColor: 'black' }}>
          <AssistantIcon style={{ fontSize: 20, color: 'white' }} />
        </Avatar>
      );
    }

    const logoUrl = getAssistantLogoUrl(assistant);
    return (
      <Avatar className={classes.avatar} src={logoUrl} alt={assistant.name}>
        {assistant.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  const getMenuItemAvatar = (assistant: ConversationAssistant) => {
    if (isLocalAssistantId(assistant.id)) {
      const logoUrl = getAssistantLogoUrl(assistant);
      if (logoUrl) {
        return (
          <Avatar
            className={classes.menuAvatar}
            src={logoUrl}
            alt={assistant.name}
          />
        );
      }
      return (
        <Avatar
          className={classes.menuAvatar}
          style={{ backgroundColor: 'black' }}
        >
          <AssistantIcon style={{ fontSize: 16, color: 'white' }} />
        </Avatar>
      );
    }

    return (
      <Avatar
        className={classes.menuAvatar}
        src={assistant.logoUrl}
        alt={assistant.name}
      >
        {assistant.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  if (!currentAssistant) {
    return null;
  }

  return (
    <>
      <IconButton
        className={classes.selectorButton}
        onClick={handleClick}
        disabled={disabled}
        aria-label="Select assistant"
        aria-haspopup="true"
      >
        <Box className={classes.avatarWrapper}>
          {getAssistantAvatar(currentAssistant)}
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        className={classes.menu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {allAssistants.featured.length > 0 && [
          <Typography key="featured-header" className={classes.sectionHeader}>
            Featured
          </Typography>,
          ...allAssistants.featured.map(assistant => (
            <MenuItem
              key={assistant.id}
              className={classes.menuItem}
              onClick={() => handleSelectAssistant(assistant)}
              selected={currentAssistant?.id === assistant.id}
            >
              <ListItemIcon>{getMenuItemAvatar(assistant)}</ListItemIcon>
              <ListItemText>
                <Typography className={classes.assistantName}>
                  {assistant.name}
                </Typography>
              </ListItemText>
            </MenuItem>
          )),
        ]}

        {allAssistants.favorited.length > 0 && [
          allAssistants.featured.length > 0 && (
            <Divider key="favorites-divider" />
          ),
          <Typography key="favorites-header" className={classes.sectionHeader}>
            Favorites
            <StarIcon className={classes.starIcon} />
          </Typography>,
          ...allAssistants.favorited.map(assistant => (
            <MenuItem
              key={assistant.id}
              className={classes.menuItem}
              onClick={() => handleSelectAssistant(assistant)}
              selected={currentAssistant?.id === assistant.id}
            >
              <ListItemIcon>{getMenuItemAvatar(assistant)}</ListItemIcon>
              <ListItemText>
                <Typography className={classes.assistantName}>
                  {assistant.name}
                </Typography>
              </ListItemText>
            </MenuItem>
          )),
        ]}
      </Menu>
    </>
  );
};
